import React, { useMemo, useEffect, useRef, useState } from "react";
import { useListForHomeQuery } from "../../../api/banners.api";
import LoadingComponent from "../../../common/Loading/Loading.component";
import { mediaPreloader } from "../../../utils/preloader";
import "./Banner.css";

// Individual banner row — own state so each item tracks its own load/play
const BannerRow = React.memo(({ item }) => {
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  // Preload this banner's media
  useEffect(() => {
    const assets = [];
    if (item.category === "image") {
      if (item.desktopImage) assets.push({ type: "image", src: item.desktopImage });
      if (item.mobileImage)  assets.push({ type: "image", src: item.mobileImage });
    } else {
      if (item.desktopVideo) assets.push({ type: "video", src: item.desktopVideo });
      if (item.mobileVideo)  assets.push({ type: "video", src: item.mobileVideo });
    }
    if (assets.length > 0) {
      mediaPreloader.batchPreload(assets).catch(() => {});
    }
  }, [item]);

  // Pause / play video based on visibility
  useEffect(() => {
    if (item.category !== "video") return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!videoRef.current) return;
        if (entry.isIntersecting) {
          videoRef.current.play().catch(() => {});
        } else {
          videoRef.current.pause();
        }
      },
      { rootMargin: "300px 0px", threshold: 0.05 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [item.category]);

  // IMAGE / VIDEO layer
  const ImageLayer =
    item.category === "video" ? (
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        className="banner-media-element"
        style={{ opacity: assetsLoaded ? 1 : 0, transition: "opacity 0.4s ease" }}
        onLoadedData={() => setAssetsLoaded(true)}
      >
        {item.mobileVideo && (
          <source media="(max-width: 600px)" src={item.mobileVideo} />
        )}
        <source src={item.desktopVideo || item.mobileVideo} />
      </video>
    ) : (
      <picture className="banner-picture">
        {item.mobileImage && (
          <source media="(max-width: 600px)" srcSet={item.mobileImage} />
        )}
        <img
          src={item.desktopImage || item.mobileImage}
          alt={item.title || "Banner"}
          className="banner-media-element"
          style={{ opacity: assetsLoaded ? 1 : 0, transition: "opacity 0.4s ease" }}
          onLoad={() => setAssetsLoaded(true)}
        />
      </picture>
    );

  return (
    <section ref={containerRef} className="banner-section" aria-label={item.title || "Banner"}>
      <div className="banner-wrapper">

        {/* BACKGROUND: full-width image / video */}
        <div className="banner-media">
          {ImageLayer}
        </div>

        {/* OVERLAY: content sits on top */}
        <div className="banner-overlay">
          <div className="banner-content">

            {/* subtitle */}
            {/* {item.subtitle && (
              <span className="banner-subtitle">{item.subtitle}</span>
            )} */}

            {/* divider */}
            {/* <div className="banner-divider"></div> */}

            {/* title */}
            {item.title && (
              <h1 className="banner-title">{item.title}</h1>
            )}

            {/* description */}
            {item.content && (
              <p className="banner-description">{item.content}</p>
            )}

            {/* button */}
            {item.button && (
              <a href={item.link || "#"}>
                <button className="banner-btn">{item.button}</button>
              </a>
            )}

          </div>
        </div>

      </div>
    </section>
  );
});

BannerRow.displayName = "BannerRow";

// ─── Main component ─────────────────────────────────────────────────────────
const Banner = () => {
  const { data, isLoading } = useListForHomeQuery();

  const banners = useMemo(() => data?.result ?? [], [data]);

  if (isLoading && !data) return <LoadingComponent />;
  if (!banners.length) return <div>No banners available</div>;

  return (
    <>
      {banners.map((item, index) => (
        <BannerRow key={item._id || index} item={item} index={index} />
      ))}
    </>
  );
};

export default React.memo(Banner);



